import { DialogService } from './dialog.service';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { InjectorInstance } from '../app.module';
import { FormOpeningMode } from '../models/enums.model';
import { NamirialPayLinkDTO } from '../models/dueInvoice.model';

@Injectable({ providedIn: 'root' })
export class UtilityService {

  static translate: TranslateService = null;

  /**
  * Utilizza l'InjectorInstance definita nell'app.module per iniettare i servizi che sono necessari in questa classe.
  * Il metodo è stato necessario perchè questo servizio espone metodi statici quindi non sarebbe stato possibile usare l'inject con il costruttore della classe
  **/
  private static injectServices() {
    if (this.translate == undefined || this.translate == null)
      this.translate = InjectorInstance.get<TranslateService>(TranslateService);
  }

  /**
   * Restituisce la modalità di apertura di una forma sulla base della configurazione dell'ActivatedRoute.
   * In particolare se è presente un queryParams chiamato view con valore TRUE allora la modalità di apertura è VISUALIZZAZIONE (quindi sarà tutto disabilitato)
   * se invece nella rotta è presente il parametro id allora la modalità di apertura è EDIT altrimenti è ADD
   **/
  static getFormOpeningMode(currentActivateRoute: ActivatedRoute): FormOpeningMode {
    let formOpeningMode: FormOpeningMode;
    if (currentActivateRoute.queryParams && currentActivateRoute.queryParams["value"] && currentActivateRoute.queryParams["value"]["view"])
      formOpeningMode = FormOpeningMode.View;
    else if (currentActivateRoute.snapshot.params["id"] != null)
      formOpeningMode = FormOpeningMode.Edit;
    else
      formOpeningMode = FormOpeningMode.Add;

    return formOpeningMode;
  }

  /**
   * Gestisce l'abilitazione/disabilitazione di tutti i controlli della form passata come parametro.
   * Questo metodo è usato su tutte le interfacce per le quali è necessario disabilitare i controlli della form
   * in base ai permessi dell'utente
   * @param form FormGroup per cui gestire lo stato dei FormControl
   * @param formMode Tipo di apertura della form (Add, Edit, View)
   **/
  static setFormControlStatus(activateRoute: ActivatedRoute, form: UntypedFormGroup) {
    if (!form || !form.controls)
      return;

    let formMode: FormOpeningMode = this.getFormOpeningMode(activateRoute);

    //Se sono in modalità 'Visualizza' a prescindere disabilito la form
    if (formMode == FormOpeningMode.View)
      form.disable();
  }

  //Costruzione dell'identificativo SEZ/0000000
  static BuildXMLIdentifier(identifier, section) {
    var xmlIdentifier = section != null && section != "" ? section + "/" : "";
    if (identifier == null) {
      DialogService.Error("E' obbligatorio compilare il numero del documento")
    }
    var identifierOrigine = identifier.toString();

    for (var i = 0; i < 7 - identifierOrigine.length; i++) {
      xmlIdentifier += "0";
    }

    xmlIdentifier += identifierOrigine;
    return xmlIdentifier;

  }

  static DateWithoutTime(currentDate: Date) {
    var d = new Date(currentDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  static DateToUTC(filterDate: Date, dayAdd: number) {
    var utcDate = new Date(filterDate);
    
    if (dayAdd != 0) {
      utcDate.setDate(utcDate.getDate() + 1);
    }
    
    utcDate = new Date(Date.UTC(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate()));                      

    
    return utcDate;
  }

  /**
  * Per la dinamicità delle proprietà, per utilizzare l'ordinamento di un'array data una specifica proprietà seguire questo esempio:
  * array.sort(UtilityService.dynamicSort("NameProperty"));
  */
  static dynamicSort(property) {
    var sortOrder = 1;
    if (property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
    }
    return function (a, b) {
      var result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return result * sortOrder;
    }
  }

  /**
   * Ritorna il numero inserito nella cella relativa a competenze o prezzo unitario in modo che
   * il campo sia popolato all'interno dell'html.
   * Se non riesce a fare il parsing di ciò che è stato scritto nel componente Input(se è una lettera ad esempio)
   * Ritorna il prezzo dell'oggetto selezionato (se esiste) altrimenti 0
   * @param event Evento relativo al focusout
   * @param element Riga della fattura da cui parte l'evento
   * @returns
   */
  static setPrice(event, element = null, decimal = 5) {
    if (event != null && !Number.isNaN(parseInt(event.target.value))) {
      let price = event.target.value.replace(',', '.');
      return parseFloat(price).toFixed(decimal);
    } else if (element.Product != null && element.Product.FullObject != null) {
      return element.Product.FullObject.Price.toFixed(decimal);
    } else {
      return 0;
    }
  }

  static getNumber(element: number | string): number {
    if (element != null)
      return Number(element);
  }

  /**
   * Nel caso il valore derivante dal focusout sia un numero lo ritorna, altrimenti ritorna null
   * @param event Evento derivante dal focusout
   */
  static checkIfNumber(event) {
    if (event != null && Number.isInteger(Number.parseInt(event.target.value)))
      return Number.parseInt(event.target.value);
    else
      return null;
  }
  /** Metodo per filtrare l'input degli oggetti
  * input permette di intercettare qualunque tipo di input anche la scrittura manuale attraverso tablet 
  * Riceve un parametro tipoinput che permetterà di poter definire cosa filtrare al seconda del tipo di dato che vogliamo imputare 
  * es. 0 numerico intero
  *     1 numerico con , e .
  **/
  static changeInput(event, tipoinput) {
    switch (tipoinput) {
      case 0: /*' NUMERICI INTERI'*/
        event.target.value = event.target.value
          .replace(/[^0-9]/g, '')
          .replace(']', '')
          .replace('[', '')
          .replace('\\', '')
          .replace('_', '')
          .replace(/(\..*)\./g, '$1');
        break;

      case 1: /*'NUMERICI CON VIRGOLA E PUNTO'*/
        event.target.value = event.target.value
          .replace(/[^0-9.,]/g, '')
          .replace(']', '')
          .replace('[', '')
          .replace('\\', '')
          .replace('_', '')
          .replace(/(\..*)\./g, '$1');
        break;
      case 2: /*' NUMERICI e LETTERE'*/
        event.target.value = event.target.value
          .replace(/[^a-zA-z0-9]/g, '')
          .replace(/(\..*)\./g, '$1')
          .replace(']', '')
          .replace('[', '')
          .replace('\\', '')
          .replace('_', '');
        break;
      case 3: /*' NUMERICI e LETTERE MAIUSCOLE'*/
        event.target.value = event.target.value
          .replace(/[^a-zA-z0-9]/g, '')
          .replace(/(\..*)\./g, '$1')
          .replace(']', '')
          .replace('[', '')
          .replace('\\', '')
          .replace('_', '').toUpperCase();
        break;
    }
  }
  /**
   * Metodo per spostare il focus all'interno dell'input sia per le virgole che per '-' e '+'
   *
   * Esempio pratico in fattura, il punto deve essere considerato come la virgola
   * ricavo dov'è già presente la virgola, e sposto il cursore una posizione dopo.
   * N.B. questo è possibile per come funziona la direttiva ngx-currency, che predispone la virgola al numero che sto inserendo
   * @param event
   */
  static changeFocus(event) {
    //Evento simbolo ','
    if ((event.keyCode == 190 || event.keyCode == 110)) {
      let ctrl;
      ctrl = event.target;
      ctrl.focus();
      let commaPosition = ctrl.value.indexOf(",") + 1;
      ctrl.setSelectionRange(commaPosition, commaPosition);
    }

    //Evento simbolo negativo '-'
    if (event.keyCode == 189 || event.keyCode == 109 || (event.target.value.includes('-') && event.keyCode != 190 && event.keyCode != 110 && event.keyCode != 188)) {
      let ctrl;
      ctrl = event.target;

      //Se il target è vuoto e sto inserendo il simbolo -, allora lo forzo manualmente
      if (ctrl.value == '') {
        ctrl.value = '-';
      }

      ctrl.focus();
      let commaPosition = ctrl.value.indexOf(",");
      //Applico lo spostamento di focus solo se mi trovo dopo la virgola oppure nel caso il focus si trovi alla fine del value
      if (commaPosition > ctrl.selectionEnd || ctrl.value.length == ctrl.selectionEnd)
        ctrl.setSelectionRange(commaPosition, commaPosition);
      // let minusPosition = ctrl.value.indexOf("-") + 1;
      // ctrl.setSelectionRange(minusPosition, minusPosition);
    }

    //Evento simbolo positivo '+'
    if (event.keyCode == 187) {
      let ctrl;
      ctrl = event.target;
      ctrl.focus();
      let plusPosition = ctrl.value.indexOf("+") + 1;
      ctrl.setSelectionRange(plusPosition, plusPosition);
    }
  }

   static controllaPartitaIVA(pi):string {
    {
      if (pi!=null)
        pi = pi.replace(/\s/g, "");
      else
        return "Vuoto.";

      if( pi.length === 0 )
        return "Vuoto.";
      else if( pi.length !== 11 )
        return "Lunghezza errata.";
      if( ! /^[0-9]{11}$/.test(pi) )
        return "Caratteri non ammessi.";
      var s = 0;
      for(var i = 0; i < 11; i++ ){
        var n = pi.charCodeAt(i) - "0".charCodeAt(0);
        if( (i & 1) === 1 ){
          n *= 2;
          if( n > 9 )
            n -= 9;
        }
        s += n;
      }
      if( s % 10 !== 0 )
        return "Non valida o errata.";
      return null;
    }
  }
  static controllaCodiceFiscaleTempraneo(cf):string {
    {
      if( ! /^[0-9]{11}$/.test(cf) )
			return "Caratteri non ammessi.";
		var s = 0;
		for(var i = 0; i < 11; i++ ){
			var n = cf.charCodeAt(i) - "0".charCodeAt(0);
			if( (i & 1) === 1 ){
				n *= 2;
				if( n > 9 )
					n -= 9;
			}
			s += n;
		}
		if( s % 10 !== 0 )
			return "Non valido.";
		return null;
    }
  }
  static controllaCodiceFiscaleRegolare(cf):string {
    {
      if( ! /^[0-9A-Z]{16}$/.test(cf) )
			return "Caratteri non ammessi.";
		var s = 0;
		var even_map = "BAFHJNPRTVCESULDGIMOQKWZYX";
		for(var i = 0; i < 15; i++){
			var c = cf[i];
			var n = 0;
			if( "0" <= c && c <= "9" )
				n = c.charCodeAt(0) - "0".charCodeAt(0);
			else
				n = c.charCodeAt(0) - "A".charCodeAt(0);
			if( (i & 1) === 0 )
				n = even_map.charCodeAt(n) - "A".charCodeAt(0);
			s += n;
		}
		if( s%26 + "A".charCodeAt(0) !== cf.charCodeAt(15) )
			return "Non valido.";
		return null;
    }
  }

  static controllaCodiceFiscale(cf):string {
    {
      if (cf!=null)
        cf = cf.replace(/\s/g, "").toUpperCase();
      return "Vuoto.";
      
      if( cf.length === 0 )
        return "Vuoto.";
      else if( cf.length === 16 )
        return this.controllaCodiceFiscaleRegolare(cf);
      else if( cf.length === 11 )
        return this.controllaCodiceFiscaleTempraneo(cf);
      else
        return "Lunghezza errata.";
    }
  }
  /**
   * Metodo per svuotare i query parameters in modo da farglieli sempre riprendere dalla rotta corrente
   * può essere necessario perchè nel reload della pagina si possono riscontrare problemi con la concatenazione di quelli esistenti o con la sostituzione del simbolo "?" che identifica i query params
   */
  clearQueryParameters(activatedRoute: ActivatedRoute, router: Router) {
    var snapshot = activatedRoute.snapshot;
    const params = { ...snapshot.queryParams };
    delete params.pos
    router.navigate([], { queryParams: params });
  }

  static downLoadFile(data: any, type: string, fileName: string) {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';

    let blob = new Blob([data.body], { type: type });
    let url = window.URL.createObjectURL(blob);

    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  static convertDataURIToBinary(dataURI): Uint8Array {
    let binaryString = window.atob(dataURI);
    let binaryLen = binaryString.length;
    let bytes = new Uint8Array(binaryLen);
    for (var i = 0; i < binaryLen; i++) {
      let ascii = binaryString.charCodeAt(i);
      bytes[i] = ascii;
    }
    return bytes;
  }

  GetEnumArray(enumObject: any, isMultilanguage:boolean,translate:TranslateService,prefix:string ): Array<any> {
    const enumArray = Object.keys(enumObject)
       .filter(key => !isNaN(Number(enumObject[key as any])))
       .map(key => ({ id: enumObject[key as any], key, value: (isMultilanguage && translate ? translate.instant(prefix + key) : key) }));
   
     return enumArray;
   }
    
   static isDocumentTypeAutoFattura(documentType: string): boolean {

    let isAutoFattura = false;
    // const allowedDocument = ["TD16", "TD17", "TD18", "TD19", "TD20", "TD21", "TD22", "TD23", "TD28", "TD29"];

    // if (documentType != null && allowedDocument.some(code => documentType.startsWith(code))) {
    //   isAutoFattura = true;
    // }
    
    return isAutoFattura;
   }
     

  static checkNamirialPayLinkValidity(expireDate: Date, daysLink: number): number {
    
    const referenceDate = new Date();
    referenceDate.setHours(0, 0, 0, 0);
    referenceDate.setDate(referenceDate.getDate() + daysLink);
    
    // 2. Pulizia ore su expireDate
    const dateToCheck = new Date(expireDate);
    dateToCheck.setHours(0, 0, 0, 0);
  
    // 3. Differenza in giorni
    const diffDays = (dateToCheck.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24);
    
    // 4. Logica
    if (diffDays > 0) {
      return 1; // data troppo avanti
    } else if (diffDays > -4) {
      return 2; // troppo vicina
    } else {
      return 0; // tutto a posto
    }

  }
}
